<template>
  <div class="api-key-manager">
    <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
      <h5 class="mb-0"><i class="fas fa-key mr-2"></i>API Keys สำหรับเชื่อมต่อระบบของคลินิก</h5>
      <b-button variant="outline-primary" size="sm" :href="docsUrl" target="_blank" rel="noopener noreferrer">
        อ่านคู่มือ API
      </b-button>
    </div>
    <p class="text-muted">
      ใช้ API Key ดึงข้อมูลใบเสร็จ นัดหมาย และคนไข้ (อ่านอย่างเดียว) ไปใช้ใน Google Sheets, ERP หรือโปรแกรมของคลินิก
    </p>

    <!-- Quota -->
    <b-card class="shadow-sm border-0 mb-3">
      <div class="font-weight-bold mb-1">การเรียกใช้ API เดือนนี้ (รวมทั้งคลินิก)</div>
      <div v-if="usageError" class="text-muted small">ยังอ่านจำนวนการใช้งานไม่ได้ในขณะนี้</div>
      <div v-else-if="quota">
        <div class="h5 mb-1">
          <template v-if="quota.percent === null">{{ quota.usedText }} / {{ quota.limitText }}</template>
          <template v-else>{{ quota.usedText }} / {{ quota.limitText }} ครั้ง</template>
        </div>
        <b-progress v-if="quota.percent !== null" :value="quota.percent" :max="100" :variant="quota.variant" height="8px" />
        <div class="text-muted small mt-1">เดือน {{ quota.period }}</div>
      </div>
    </b-card>

    <!-- One-time key box -->
    <b-alert v-if="revealed" show variant="warning" class="mb-3">
      <div class="font-weight-bold mb-2">API Key — แสดงครั้งเดียวเท่านั้น</div>
      <div class="mb-2">{{ revealed.name }}</div>
      <b-input-group class="mb-2">
        <b-form-input ref="keyInput" readonly :value="revealed.apiKey" @focus="$event.target.select()" />
        <b-input-group-append>
          <b-button variant="outline-secondary" @click="copyKey">
            <i class="fas fa-copy mr-1"></i>คัดลอก
          </b-button>
        </b-input-group-append>
      </b-input-group>
      <div class="mb-2">คัดลอกเก็บไว้ในที่ปลอดภัย ปิดกล่องนี้แล้วจะดูคีย์นี้อีกไม่ได้ ถ้าทำหายให้กด "เปลี่ยนคีย์"</div>
      <b-button size="sm" variant="warning" @click="dismissRevealed">บันทึกคีย์แล้ว ปิดกล่องนี้</b-button>
    </b-alert>

    <!-- Create form -->
    <b-card class="shadow-sm border-0 mb-3">
      <h6 class="mb-3">สร้าง API Key ใหม่</h6>
      <b-form novalidate @submit.prevent="submitCreate">
        <b-form-group label="ชื่อคีย์" label-for="apikey-name" :state="fieldState('name')" :invalid-feedback="errors.name">
          <b-form-input
            id="apikey-name"
            v-model="form.name"
            maxlength="255"
            placeholder="เช่น Google Sheets รายงานรายวัน"
            :state="fieldState('name')"
          />
        </b-form-group>

        <b-form-group label="ข้อมูลที่อนุญาตให้อ่าน" :state="fieldState('scopes')">
          <b-form-checkbox-group v-model="form.scopes" stacked :state="fieldState('scopes')">
            <b-form-checkbox v-for="scope in scopes" :key="scope" :value="scope">{{ scopeLabel(scope) }}</b-form-checkbox>
          </b-form-checkbox-group>
          <b-form-invalid-feedback :state="fieldState('scopes')">{{ errors.scopes }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          v-if="level === 'clinic'"
          label="สาขา"
          label-for="apikey-branch"
          :state="fieldState('branchId')"
          :invalid-feedback="errors.branchId"
        >
          <b-form-select id="apikey-branch" v-model="form.branchId" :options="branchOptions" :state="fieldState('branchId')" />
        </b-form-group>

        <b-form-group label="จำกัด IP ที่เรียกใช้ได้ (ไม่บังคับ)" label-for="apikey-ip">
          <b-form-textarea id="apikey-ip" v-model="form.ipText" rows="3" />
          <small class="form-text text-muted">
            ใส่ IP หรือช่วง CIDR บรรทัดละ 1 รายการ เช่น 203.0.113.10 หรือ 203.0.113.0/24 — เว้นว่างไว้ = ใช้ได้ทุก IP
          </small>
        </b-form-group>

        <b-form-group v-if="hasPatientScope" :state="fieldState('acceptTerms')">
          <b-form-checkbox v-model="form.acceptTerms" :state="fieldState('acceptTerms')">
            คลินิกในฐานะผู้ควบคุมข้อมูลส่วนบุคคลยอมรับข้อตกลงการประมวลผลข้อมูลคนไข้ตาม PDPA และจะใช้ข้อมูลที่ได้รับผ่าน API ตามวัตถุประสงค์ที่แจ้งคนไข้ไว้เท่านั้น
          </b-form-checkbox>
          <b-form-invalid-feedback :state="fieldState('acceptTerms')">{{ errors.acceptTerms }}</b-form-invalid-feedback>
        </b-form-group>

        <b-alert v-if="submitError" show variant="danger">{{ submitError }}</b-alert>

        <b-button type="submit" variant="primary" :disabled="!canMutate">
          <b-spinner v-if="isSubmitting" small class="mr-1" />สร้างคีย์
        </b-button>
        <small v-if="revealed" class="text-muted ml-2">ปิดกล่องคีย์ด้านบนก่อนสร้างหรือเปลี่ยนคีย์อื่น</small>
      </b-form>
    </b-card>

    <!-- List -->
    <b-alert v-if="loadError" show variant="danger">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-2" @click="load">ลองใหม่</b-button>
    </b-alert>
    <b-card v-else class="shadow-sm border-0">
      <div v-if="isLoading" class="text-center py-4"><b-spinner /></div>
      <b-table v-else hover responsive show-empty empty-text="ยังไม่มี API Key" :items="keys" :fields="fields">
        <template #cell(apiKeyPrefix)="row"><code>{{ row.item.apiKeyPrefix }}…</code></template>
        <template #cell(scopes)="row">
          <b-badge v-for="s in row.item.scopes || []" :key="s" variant="info" class="mr-1">{{ scopeLabel(s) }}</b-badge>
        </template>
        <template #cell(branchName)="row">{{ row.item.branchName || "ทุกสาขา" }}</template>
        <template #cell(ipAllowlist)="row">{{ formatIps(row.item.ipAllowlist) }}</template>
        <template #cell(lastUsedAt)="row">{{ formatLastUsed(row.item.lastUsedAt) }}</template>
        <template #cell(active)="row">
          <b-form-checkbox
            switch
            :key="`${row.item.id}-${reloadTick}`"
            :checked="row.item.active"
            :disabled="!canMutate"
            @change="v => toggleActive(row.item, v)"
          />
        </template>
        <template #cell(actions)="row">
          <b-button-group size="sm">
            <b-button variant="outline-secondary" title="เปลี่ยนคีย์" :disabled="!canMutate" @click="confirmRotate(row.item)">
              <i class="fas fa-sync-alt"></i>
            </b-button>
            <b-button variant="outline-danger" title="ลบ" :disabled="!canMutate" @click="confirmDelete(row.item)">
              <i class="fas fa-trash-alt"></i>
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-card>

    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";
import Dialog from "@/components/modal/Dialog";
import { PUBLIC_API_DOCS_URL } from "@/config/publicApi";
import {
  API_KEY_SCOPES,
  PATIENT_SCOPE,
  ALL_BRANCHES,
  scopeLabel,
  validateApiKeyForm,
  buildCreatePayload,
  describeApiQuota,
  canStartKeyMutation,
  apiKeyErrorMessage
} from "@/utils/apiKeys";

const emptyForm = () => ({ name: "", scopes: [], branchId: "", ipText: "", acceptTerms: false });

// Hosts must :key this component by clinicUrl (clinic) or `${clinicUrl}/${branchUrl}` (branch);
// it has no route watchers. The plaintext key lives only in `revealed` (local data).
export default {
  name: "ApiKeyManager",
  components: { Dialog },
  props: {
    level: { type: String, required: true, validator: v => ["clinic", "branch"].includes(v) },
    clinicUrl: { type: String, required: true },
    branchUrl: { type: String, default: null }
  },
  data() {
    return {
      docsUrl: PUBLIC_API_DOCS_URL,
      scopes: API_KEY_SCOPES,
      keys: [],
      isLoading: false,
      loadError: "",
      usage: null,
      usageError: false,
      branches: [],
      form: emptyForm(),
      errors: {},
      submitted: false,
      submitError: "",
      isSubmitting: false,
      revealed: null,
      busyKeyId: null,
      reloadTick: 0 // forces switches to re-render from server state (reverts a failed toggle)
    };
  },
  computed: {
    quota() {
      return describeApiQuota(this.usage);
    },
    canMutate() {
      return canStartKeyMutation({ revealed: this.revealed, isSubmitting: this.isSubmitting, busyKeyId: this.busyKeyId });
    },
    hasPatientScope() {
      return this.form.scopes.includes(PATIENT_SCOPE);
    },
    branchOptions() {
      return [
        { value: "", text: "— เลือกสาขา —", disabled: true },
        { value: ALL_BRANCHES, text: "ทุกสาขา" },
        ...this.branches.map(b => ({ value: b.id, text: b.name }))
      ];
    },
    fields() {
      const f = [
        { key: "name", label: "ชื่อ" },
        { key: "apiKeyPrefix", label: "คีย์" },
        { key: "scopes", label: "ข้อมูลที่อ่านได้" }
      ];
      if (this.level === "clinic") f.push({ key: "branchName", label: "สาขา" });
      f.push(
        { key: "ipAllowlist", label: "IP ที่อนุญาต" },
        { key: "lastUsedAt", label: "ใช้งานล่าสุด" },
        { key: "active", label: "เปิดใช้งาน" },
        { key: "actions", label: "" }
      );
      return f;
    }
  },
  watch: {
    revealed(val, old) {
      const on = !!val;
      this.$emit("revealed-change", on);
      if (on === !!old) return;
      if (on) window.addEventListener("beforeunload", this.onBeforeUnload);
      else window.removeEventListener("beforeunload", this.onBeforeUnload);
    },
    "form.scopes"(scopes) {
      if (!scopes.includes(PATIENT_SCOPE)) this.form.acceptTerms = false;
      if (this.submitted) this.revalidate();
    },
    "form.name"() {
      if (this.submitted) this.revalidate();
    },
    "form.branchId"() {
      if (this.submitted) this.revalidate();
    },
    "form.acceptTerms"() {
      if (this.submitted) this.revalidate();
    }
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    this.revealed = null;
  },
  methods: {
    ...mapActions("moduleApiKey", ["fetchKeys", "createKey", "updateKey", "rotateKey", "deleteKey", "fetchUsage", "fetchClinicBranches"]),
    scopeLabel,
    onBeforeUnload(e) {
      e.preventDefault();
      e.returnValue = "";
    },
    ctx() {
      return { level: this.level, clinicUrl: this.clinicUrl, branchUrl: this.branchUrl };
    },
    toast(variant, text) {
      this.$refs.Dialog.showToast(variant, text);
    },
    fieldState(key) {
      return this.errors[key] ? false : null;
    },
    revalidate() {
      this.errors = validateApiKeyForm({ level: this.level, form: this.form });
    },
    formatIps(list) {
      return Array.isArray(list) && list.length ? list.join(", ") : "ทุก IP";
    },
    formatLastUsed(v) {
      return v ? moment(v).locale("th").format("DD MMM YY HH:mm") : "ยังไม่เคยใช้";
    },
    async loadKeys() {
      try {
        this.keys = await this.fetchKeys(this.ctx());
        this.loadError = "";
        this.reloadTick += 1;
      } catch (err) {
        this.loadError = apiKeyErrorMessage(err, "โหลดรายการ API Key ไม่สำเร็จ", { notFound: false });
      }
    },
    async loadUsage() {
      try {
        this.usage = await this.fetchUsage({ clinicUrl: this.clinicUrl });
        this.usageError = false;
      } catch (err) {
        this.usageError = true;
      }
    },
    async loadBranches() {
      try {
        this.branches = (await this.fetchClinicBranches({ clinicUrl: this.clinicUrl })) || [];
      } catch (err) {
        this.branches = [];
      }
    },
    async load() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.loadKeys(),
          this.loadUsage(),
          this.level === "clinic" ? this.loadBranches() : Promise.resolve()
        ]);
      } finally {
        this.isLoading = false;
      }
    },
    // List reloads never touch `revealed` and don't flash the table spinner.
    async reloadKeys() {
      await Promise.all([this.loadKeys(), this.loadUsage()]);
    },
    async submitCreate() {
      if (!this.canMutate) return;
      this.submitted = true;
      this.submitError = "";
      this.revalidate();
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      try {
        const res = await this.createKey({
          ...this.ctx(),
          payload: buildCreatePayload({ level: this.level, form: this.form })
        });
        this.revealed = { name: res.name, apiKey: res.apiKey };
        this.form = emptyForm();
        this.errors = {};
        this.submitted = false;
        this.toast("success", "สร้างคีย์สำเร็จ");
        await this.reloadKeys();
      } catch (err) {
        this.submitError = apiKeyErrorMessage(err);
      } finally {
        this.isSubmitting = false;
      }
    },
    async copyKey() {
      try {
        await navigator.clipboard.writeText(this.revealed.apiKey);
        this.toast("success", "คัดลอกคีย์แล้ว");
      } catch (err) {
        this.toast("warning", "คัดลอกไม่สำเร็จ กรุณาเลือกข้อความในช่องแล้วคัดลอกเอง");
        const input = this.$refs.keyInput && (this.$refs.keyInput.$el || this.$refs.keyInput);
        if (input && input.focus) input.focus();
      }
    },
    dismissRevealed() {
      this.revealed = null;
    },
    async toggleActive(item, value) {
      if (!this.canMutate) {
        this.reloadTick += 1; // snap the switch back
        return;
      }
      this.busyKeyId = item.id;
      try {
        await this.updateKey({ ...this.ctx(), keyId: item.id, changes: { active: value } });
        this.toast("success", value ? "เปิดใช้งานคีย์แล้ว" : "ปิดใช้งานคีย์แล้ว");
      } catch (err) {
        this.toast("danger", apiKeyErrorMessage(err));
      } finally {
        // Always reload: reverts the switch on failure.
        await this.reloadKeys();
        this.busyKeyId = null;
      }
    },
    async confirmRotate(item) {
      if (!this.canMutate) return;
      const result = await this.$refs.Dialog.showAlertConfirm(
        "เปลี่ยนคีย์",
        `ต้องการเปลี่ยนคีย์ของ "${item.name}"? คีย์เดิมจะใช้ไม่ได้ทันที สคริปต์ที่ใช้คีย์เดิมต้องเปลี่ยนเป็นคีย์ใหม่`
      );
      if (!result || !result.isConfirmed) return;
      // The dialog is async: re-check that no create started or key was revealed meanwhile.
      if (!this.canMutate) return;
      this.busyKeyId = item.id;
      try {
        const res = await this.rotateKey({ ...this.ctx(), keyId: item.id });
        this.revealed = { name: item.name, apiKey: res.apiKey };
        this.toast("success", "เปลี่ยนคีย์สำเร็จ");
      } catch (err) {
        this.toast("danger", apiKeyErrorMessage(err));
      } finally {
        await this.reloadKeys();
        this.busyKeyId = null;
      }
    },
    async confirmDelete(item) {
      if (!this.canMutate) return;
      const result = await this.$refs.Dialog.showAlertConfirm(
        "ลบคีย์",
        `ต้องการลบ "${item.name}"? สคริปต์ที่ใช้คีย์นี้จะเรียก API ไม่ได้อีก และกู้คืนไม่ได้`
      );
      if (!result || !result.isConfirmed || !this.canMutate) return;
      this.busyKeyId = item.id;
      try {
        await this.deleteKey({ ...this.ctx(), keyId: item.id });
        this.toast("success", "ลบคีย์แล้ว");
      } catch (err) {
        this.toast("danger", apiKeyErrorMessage(err));
      } finally {
        await this.reloadKeys();
        this.busyKeyId = null;
      }
    }
  }
};
</script>
